import PropTypes from "prop-types";
import * as React from "react";

import { isChildHtmlElement, isChildOfType, pullContext } from "../../TypingUtils";
import {
    UnitLayoutingContext,
    unitLayoutingContextTypes,
} from "../../FormParts/Unit/Internals/UnitBorderContext/UnitLayoutingContext";

import { Colgroup } from "./Colgroup";
import { Root, Table } from "./StickyTableSidebar.styles";
import { StickyTableBodyRowContainer } from "./Body/StickyTableBodyRowContainer";

export interface SidebarProps {
    children: React.ReactNode;
    className?: string;
    columnWidth: number[];
    heightRows?: number[];
    horizontalScroll: number;
    paddingBottom: number;
    width: number;
}

interface SidebarContext extends UnitLayoutingContext {
    setSidebarBody: (el: null | HTMLTableSectionElement) => void;
}

export class StickyTableSidebar extends React.Component<SidebarProps> {
    public static contextTypes = {
        ...unitLayoutingContextTypes,
        setSidebarBody: PropTypes.func,
    };

    public shouldComponentUpdate(nextProps: SidebarProps): boolean {
        return (
            nextProps.horizontalScroll !== this.props.horizontalScroll ||
            nextProps.width !== this.props.width ||
            nextProps.heightRows !== this.props.heightRows
        );
    }

    public render(): React.ReactNode {
        const children = React.Children.map(this.props.children ?? [], (child, i) => {
            if (isChildHtmlElement(child, "tr")) {
                const p = child.props;
                const props = this.props.heightRows
                    ? {
                          style: { ...p.style, height: this.props.heightRows[i] },
                      }
                    : {};

                return React.cloneElement(child, props);
            }
            if (isChildOfType(child, StickyTableBodyRowContainer)) {
                const p = child.props;
                const props = this.props.heightRows
                    ? {
                          style: { ...p.style, height: this.props.heightRows[i] },
                      }
                    : {};

                return React.cloneElement(child, props);
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (!React.isValidElement<any>(child)) {
                return child;
            }
            return React.cloneElement(child, {
                horizontalScroll: this.props.horizontalScroll,
                columnWidth: this.props.columnWidth,
                height: this.props.heightRows ? this.props.heightRows[i] : 0,
                width: this.props.width,
                isLastRow: React.Children.count(this.props.children) - 1 === i,
            });
        });

        return (
            <Root
                sticky={Boolean(this.props.horizontalScroll)}
                style={{ paddingLeft: this.getContext().paddingLeft, paddingBottom: this.props.paddingBottom }}>
                <Table className={this.props.className}>
                    <Colgroup columnWidth={this.props.columnWidth} />
                    <tbody ref={this.refSidebarBody}>{children}</tbody>
                </Table>
            </Root>
        );
    }

    private readonly refSidebarBody = (el: null | HTMLTableSectionElement) => {
        this.getContext().setSidebarBody(el);
    };

    private getContext(): SidebarContext {
        return pullContext<SidebarContext>(this);
    }
}
